import { Component, computed, inject, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { RouterLink } from '@angular/router';
import { isFunpayLive } from '../../../../services/funpay.service';
import { AuthService } from '../../../../services/auth.service';
import { IClientSettings } from '../../../../../../../../../common/common-interfaces/client-settings';

export type PaymentProvider = 'FunPay' | 'Stripe' | 'Adyen' | 'Swish';

/** One row of `GET /organizations/:id/associated-clients`. */
export interface Venue {
    id: string;
    name: string;
    settings: IClientSettings;
}

@Component({
    selector: 'venue-card',
    standalone: true,
    imports: [NgClass, ButtonModule, TagModule, RouterLink],
    templateUrl: './venue-card.html'
})
export class VenueCardComponent {
    venue = input.required<Venue>();

    private auth = inject(AuthService);

    /** The venue whose FunPay account this one shares, if any. */
    sharedFrom = computed(() => {
        const sourceId = this.venue()?.settings?.funPayData?.linkedFromClientId;
        return sourceId ? (this.auth.clientsInOrganization().find((c) => String(c._id ?? c.id) === sourceId)?.name ?? null) : null;
    });

    private paymentSettings = computed(() => this.venue()?.settings?.paymentSettings);

    hasFunPay = computed(() => this.paymentSettings()?.paymentProvider === 'funpay' && !!this.paymentSettings()?.paymentEnabled);

    paymentEnabled = computed(() => !!this.paymentSettings()?.paymentEnabled);
    depostisEnabled = computed(() => !!this.paymentSettings()?.simplifiedPaymentMode);

    status = computed(() =>
        this.hasFunPay() ? { label: 'Active', severity: 'success' as const } : { label: 'Inactive', severity: 'secondary' as const }
    );

    /** Approved by Adyen, whether or not payments have been switched on for the venue yet. */
    isFunpayLive = computed(() => isFunpayLive(this.venue()?.settings?.funPayData?.verification));

    get payRoute() {
        const funPayActive = this.hasFunPay() || this.isFunpayLive();
        const venueId = this.venue()?.id;

        if (funPayActive && venueId) {
            return [venueId];
        } else {
            return [venueId, 'onboarding'];
        }
    }
}
